// client/src/components/pages/OrderManagement/shared/orderHelpers.ts
//
// Pure helpers shared by the Order Management pages (ST-00227, audit batch ORD): the
// status steps the server accepts, the amendment diff, error text for a failed call,
// tonnes formatting, the unit-of-measure list the database allows and the latitude /
// longitude check of the Locations form.

import type {
  ApiErrorLike,
  OrderAmendmentData,
  OrderDetail,
} from '../../../../types/clientPages/orderManagement';

/**
 * The in-app path of an order's page. Weighbridge order numbers contain "/"
 * (`Mafube(Exxaro)-to-Destone/Stamlers`), so the number is encoded into ONE path segment;
 * React Router hands `useParams().orderNo` back decoded.
 */
export function orderPath(orderNo: string): string {
  return `/order-management/orders/${encodeURIComponent(orderNo)}`;
}

/** The permission every order and master-data write needs on the server (ORD-12). */
export const ORDERS_MANAGE = 'orders.manage';

/**
 * The status steps services/orderService.ts accepts (Completed and Cancelled are final).
 * Used only when an older server sends no `allowed_transitions`.
 */
export const ORDER_TRANSITIONS: Readonly<Record<string, readonly string[]>> = {
  Active: ['In Progress', 'On Hold', 'Cancelled'],
  'In Progress': ['Completed', 'On Hold', 'Cancelled'],
  'On Hold': ['Active', 'Cancelled'],
  Completed: [],
  Cancelled: [],
};

/** The statuses the status window may offer for this order (ORD-14). */
export function allowedTransitions(
  order: Pick<OrderDetail, 'order_status' | 'allowed_transitions'>
): string[] {
  if (Array.isArray(order.allowed_transitions)) return order.allowed_transitions;
  return [...(ORDER_TRANSITIONS[order.order_status] ?? [])];
}

/** An order that can still be changed (status, amendment, line items, cancel). */
export function isOrderOpen(status: string | null | undefined): boolean {
  return status !== 'Completed' && status !== 'Cancelled';
}

/** The order types the server stores (ck_orders_type); the list filter offers what Create Order makes. */
export const ORDER_TYPE_FILTERS: readonly string[] = ['Dispatch', 'Receipt'];

/** products.unit_of_measure CHECK (ck_products_unit_of_measure) — nothing else saves (ORD-10). */
export const PRODUCT_UNITS: readonly string[] = [
  'Metric Tonnes',
  'Tons',
  'Kilograms',
  'Cubic Meters',
];
export const DEFAULT_PRODUCT_UNIT = 'Metric Tonnes';

/** Largest order size the server accepts (numeric(18,3) and utils/orderValidation). */
export const MAX_ORDER_SIZE = 999999.999;

/**
 * A readable message for a failed API call. The permission gate answers 403
 * `{ error: 'Forbidden', message: 'Insufficient permissions' }`; every other error body
 * carries `errors[]` (validation) or `error`.
 */
export function apiErrorMessage(err: unknown, fallback: string): string {
  const reason = serverReason(err);
  if (reason) return reason;
  const message = ((err ?? {}) as ApiErrorLike).message;
  return message ? `${fallback} (${message})` : fallback;
}

/**
 * Just the reason, for messages that already say what failed ("Failed to save vehicle: …"):
 * the server's reason (as apiErrorMessage), else the transport error, else "Unknown error".
 */
export function apiErrorReason(err: unknown): string {
  return serverReason(err) || ((err ?? {}) as ApiErrorLike).message || 'Unknown error';
}

/** The permission gate's 403, a validation list, `error` or `message`; null when none. */
function serverReason(err: unknown): string | null {
  const e = (err ?? {}) as ApiErrorLike;
  const data = e.response?.data;
  if (e.response?.status === 403 && (!data?.error || data.error === 'Forbidden')) {
    return 'Your role does not allow managing orders. Ask an administrator for access.';
  }
  if (Array.isArray(data?.errors) && data.errors.length > 0) return data.errors.join('; ');
  if (data?.error && data.error !== 'Forbidden') return data.error;
  if (data?.message) return data.message;
  return null;
}

/** Tonnes with at most one decimal, grouped. */
export function formatTonnes(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(Number(value))) return '—';
  return Number(value).toLocaleString(undefined, { maximumFractionDigits: 1 });
}

export interface AmendmentForm {
  order_size: string;
  order_priority: string;
}

export type AmendmentDiff = { ok: true; data: OrderAmendmentData } | { ok: false; error: string };

/**
 * Only the fields the user changed (ORD-2): the server carries every earlier amended value
 * forward, so sending an unchanged field would only add noise to the history.
 */
export function buildAmendment(
  order: Pick<OrderDetail, 'order_size' | 'order_priority'>,
  form: AmendmentForm
): AmendmentDiff {
  const data: OrderAmendmentData = {};
  const sizeText = form.order_size.trim();
  if (sizeText === '') return { ok: false, error: 'Order size is required.' };
  const size = Number(sizeText);
  if (!Number.isFinite(size) || size <= 0) {
    return { ok: false, error: 'Order size must be a number greater than 0.' };
  }
  if (size > MAX_ORDER_SIZE) {
    return { ok: false, error: 'Order size is too large (max: 999,999.999 MT).' };
  }
  if (size !== Number(order.order_size)) data.order_size = size;
  if (form.order_priority && form.order_priority !== order.order_priority) {
    data.order_priority = form.order_priority;
  }
  if (Object.keys(data).length === 0) {
    return { ok: false, error: 'Change the order size or the priority first.' };
  }
  return { ok: true, data };
}

export type LatLonCheck =
  { ok: true; latitude: number | null; longitude: number | null } | { ok: false; error: string };

/** Latitude / longitude of the Locations form (ORD-22): both or neither, in range. */
export function parseLatLon(latitudeText: string, longitudeText: string): LatLonCheck {
  const lat = latitudeText.trim();
  const lon = longitudeText.trim();
  if (lat === '' && lon === '') return { ok: true, latitude: null, longitude: null };
  if (lat === '' || lon === '') {
    return { ok: false, error: 'Enter both latitude and longitude, or leave both empty.' };
  }
  const latitude = Number(lat);
  const longitude = Number(lon);
  if (!Number.isFinite(latitude) || latitude < -90 || latitude > 90) {
    return { ok: false, error: 'Latitude must be a number from -90 to 90.' };
  }
  if (!Number.isFinite(longitude) || longitude < -180 || longitude > 180) {
    return { ok: false, error: 'Longitude must be a number from -180 to 180.' };
  }
  return { ok: true, latitude, longitude };
}
